import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Chip, Button, Grid, Paper, Stack, Divider, IconButton,
} from '@mui/material';
import {
  Close, ShoppingCart, TrendingUp, TrendingDown, CheckCircle, Warning,
  Info, PriorityHigh, AccessTime, Security, Psychology, Speed, Launch,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

export default function NotificationDetailModal({ open, notification, onClose, onMarkAsRead }) {
  const navigate = useNavigate();

  if (!notification) return null;

  const {
    id,
    title,
    message,
    reason,
    category,
    priority = 'MEDIUM',
    symbol,
    instrument_type,
    instrumentType,
    order_id,
    orderId,
    entry_price,
    entryPrice,
    current_price,
    currentPrice,
    exit_price,
    exitPrice,
    stop_loss,
    stopLoss,
    target,
    trailing_stop_loss,
    trailingStopLoss,
    confidence,
    holding_period,
    holdingPeriod,
    pnl,
    mode,
    is_read,
    isRead,
    created_at,
    createdAt,
    sent_at,
  } = notification;

  const actualEntry = entryPrice ?? entry_price;
  const actualCurrent = currentPrice ?? current_price;
  const actualExit = exitPrice ?? exit_price;
  const actualSL = stopLoss ?? stop_loss;
  const actualTarget = target;
  const actualTSL = trailingStopLoss ?? trailing_stop_loss;
  const actualHolding = holdingPeriod ?? holding_period;
  const actualSymbol = symbol;
  const actualOrderId = orderId ?? order_id;
  const isReadState = isRead ?? is_read;
  const dateString = created_at || createdAt || sent_at;

  const getPriorityColor = (p) => {
    switch (p) {
      case 'CRITICAL': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      default: return 'default';
    }
  };

  const getPriorityIcon = (p) => {
    switch (p) {
      case 'CRITICAL': return <PriorityHigh color="error" />;
      case 'HIGH': return <Warning color="warning" />;
      case 'MEDIUM': return <Info color="info" />;
      default: return <CheckCircle color="success" />;
    }
  };

  const handleAction = () => {
    if (onClose) onClose();
    if (actualSymbol || actualOrderId) {
      navigate(`/equity-orders?symbol=${actualSymbol || ''}`);
    } else {
      navigate('/trading');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: '#0f172a',
          color: '#f8fafc',
          boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
          border: '1px solid rgba(255,255,255,0.1)',
        },
      }}
    >
      <DialogTitle sx={{ p: 2.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {getPriorityIcon(priority)}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Chip
                label={priority}
                color={getPriorityColor(priority)}
                size="small"
                sx={{ fontWeight: 800, fontSize: '0.65rem' }}
              />
              {category && (
                <Chip
                  label={category}
                  variant="outlined"
                  size="small"
                  sx={{ color: '#94a3b8', borderColor: '#334155', fontSize: '0.65rem' }}
                />
              )}
              {actualSymbol && (
                <Chip
                  label={actualSymbol}
                  size="small"
                  sx={{ bgcolor: '#0284c7', color: '#fff', fontWeight: 800, fontSize: '0.7rem' }}
                />
              )}
              {mode && (
                <Chip
                  label={mode}
                  size="small"
                  color={mode === 'LIVE' ? 'success' : 'default'}
                  sx={{ fontSize: '0.65rem' }}
                />
              )}
            </Stack>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#94a3b8' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 2.5 }} />

      <DialogContent sx={{ p: 2.5 }}>
        <Stack spacing={2.5}>
          {/* Main Message Text */}
          <Paper sx={{ p: 2, bgcolor: '#1e293b', border: '1px solid #334155', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <AccessTime sx={{ fontSize: 14 }} />
              {dateString ? new Date(dateString).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'Just now'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#f1f5f9', whiteSpace: 'pre-line', lineHeight: 1.6, fontWeight: 500 }}>
              {message}
            </Typography>
          </Paper>

          {/* AI Reason Breakdown */}
          {reason && (
            <Paper sx={{ p: 2, bgcolor: 'rgba(14, 165, 233, 0.1)', border: '1px solid rgba(14, 165, 233, 0.3)', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Psychology sx={{ fontSize: 20 }} /> AI Rationale & Execution Strategy
              </Typography>
              <Typography variant="body2" sx={{ color: '#cbd5e1', whiteSpace: 'pre-line', fontSize: '0.9rem' }}>
                {reason}
              </Typography>
            </Paper>
          )}

          {/* Financial & Trade Metrics Grid */}
          {(actualEntry || actualCurrent || actualExit || actualSL || actualTarget || pnl !== undefined || confidence) && (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#94a3b8', mb: 1 }}>
                Trade & Order Parameters
              </Typography>
              <Grid container spacing={1.5}>
                {actualEntry && (
                  <Grid item xs={6} sm={4}>
                    <Paper sx={{ p: 1.5, bgcolor: '#1e293b', textAlign: 'center', border: '1px solid #334155' }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Entry Price</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#38bdf8' }}>₹{actualEntry}</Typography>
                    </Paper>
                  </Grid>
                )}
                {actualCurrent && (
                  <Grid item xs={6} sm={4}>
                    <Paper sx={{ p: 1.5, bgcolor: '#1e293b', textAlign: 'center', border: '1px solid #334155' }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Current Price</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>₹{actualCurrent}</Typography>
                    </Paper>
                  </Grid>
                )}
                {actualExit && (
                  <Grid item xs={6} sm={4}>
                    <Paper sx={{ p: 1.5, bgcolor: '#1e293b', textAlign: 'center', border: '1px solid #334155' }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Exit Price</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#f59e0b' }}>₹{actualExit}</Typography>
                    </Paper>
                  </Grid>
                )}
                {actualSL && (
                  <Grid item xs={6} sm={4}>
                    <Paper sx={{ p: 1.5, bgcolor: '#1e293b', textAlign: 'center', border: '1px solid #334155' }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Stop Loss</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444' }}>₹{actualSL}</Typography>
                    </Paper>
                  </Grid>
                )}
                {actualTarget && (
                  <Grid item xs={6} sm={4}>
                    <Paper sx={{ p: 1.5, bgcolor: '#1e293b', textAlign: 'center', border: '1px solid #334155' }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Target</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#22c55e' }}>₹{actualTarget}</Typography>
                    </Paper>
                  </Grid>
                )}
                {actualTSL && (
                  <Grid item xs={6} sm={4}>
                    <Paper sx={{ p: 1.5, bgcolor: '#1e293b', textAlign: 'center', border: '1px solid #334155' }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Trailing SL</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#38bdf8' }}>₹{actualTSL}</Typography>
                    </Paper>
                  </Grid>
                )}
                {pnl !== undefined && pnl !== null && (
                  <Grid item xs={6} sm={4}>
                    <Paper sx={{ p: 1.5, bgcolor: pnl >= 0 ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)', textAlign: 'center', border: pnl >= 0 ? '1px solid #22c55e' : '1px solid #ef4444' }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Realized / Unrealized P&L</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: pnl >= 0 ? '#22c55e' : '#ef4444' }}>
                        {pnl >= 0 ? '+' : ''}₹{pnl}
                      </Typography>
                    </Paper>
                  </Grid>
                )}
                {confidence && (
                  <Grid item xs={6} sm={4}>
                    <Paper sx={{ p: 1.5, bgcolor: '#1e293b', textAlign: 'center', border: '1px solid #334155' }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>AI Confidence</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#a855f7' }}>{confidence}%</Typography>
                    </Paper>
                  </Grid>
                )}
                {actualHolding && (
                  <Grid item xs={6} sm={4}>
                    <Paper sx={{ p: 1.5, bgcolor: '#1e293b', textAlign: 'center', border: '1px solid #334155' }}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>Expected Holding</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#cbd5e1', mt: 0.5 }}>{actualHolding}</Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mx: 2.5 }} />

      <DialogActions sx={{ p: 2.5, justifyContent: 'space-between' }}>
        <Box>
          {!isReadState && onMarkAsRead && (
            <Button
              size="small"
              onClick={() => onMarkAsRead(id)}
              sx={{ color: '#94a3b8' }}
            >
              Mark as Read
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" onClick={onClose} sx={{ color: '#94a3b8', borderColor: '#334155' }}>
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Launch />}
            onClick={handleAction}
            sx={{ fontWeight: 700, bgcolor: '#0284c7', '&:hover': { bgcolor: '#0369a1' } }}
          >
            View Trade & Orders
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
